// resources/js/router/guards.js
import { useAuth } from '@/composables/useAuth'
import { useCompassStatus } from '@/composables/useCompassStatus'
import { getControlCenterAccessSync } from '@/composables/useControlCenter'
import { getAdminStatusSync } from '@/composables/useAdminStatus'
import { useV2OnboardingLockStore } from '@/stores/v2OnboardingLock'

// Initialize auth composable once at module level
const { clearAuth } = useAuth()

// Routes that require Control Center access
const CONTROL_CENTER_ROUTES = [
  'control-center-store-details',
  'control-center-user-management',
  'control-center-roles-permissions',
  'insights-data-import',
]

// Admin route prefix — any route starting with 'admin' requires admin role
const ADMIN_ROUTE_PREFIX = 'admin'
const COMPASS_ROUTE_PREFIX = 'compassai-'
const COMPASS_PATH_PREFIX = '/compassai/'

const isCompassRoute = to => {
  const routeName = typeof to.name === 'string' ? to.name : ''

  return routeName.startsWith(COMPASS_ROUTE_PREFIX) || to.path.startsWith(COMPASS_PATH_PREFIX)
}

export const setupGuards = router => {
  router.beforeEach(async (to, from, next) => {
    const isPublic    = to.matched.some(r => r.meta.public)
    const isGuestOnly = to.matched.some(r => r.meta.guestOnly || r.meta.unauthenticatedOnly)

    // 1️⃣ public routes → always allow
    if (isPublic) {
      return next()
    }

    // 2️⃣ guest-only pages → check with backend if user is already authenticated
    if (isGuestOnly) {
      try {
        // Make a quick backend call to check if user is already authenticated
        const response = await fetch('/api/auth/check-session', {
          method: 'GET',
          credentials: 'include', // Include cookies/session
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        })

        if (response.ok) {
          const data = await response.json()
          if (data.authenticated) {
            // User is already authenticated, redirect to appropriate dashboard
            const home = { name: 'apps-v2' }

            
            return next(home)
          }
        }
      } catch (e) {
        // If check fails, assume not authenticated and continue to guest page
        console.debug('Session check failed:', e)
      }

      // Not authenticated, allow access to guest page
      return next()
    }

    // 3️⃣ Protected routes - verify session before allowing access
    try {
      const response = await fetch('/api/auth/check-session', {
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      })

      if (response.ok) {
        const data = await response.json()
        if (data.authenticated) {
          // Block Control Center routes for unauthorized roles
          if (CONTROL_CENTER_ROUTES.includes(to.name) && !getControlCenterAccessSync().hasAccess) {
            return next({ name: 'apps-v2' })
          }

          // Block admin routes for non-admin roles
          if (to.name && to.name.startsWith(ADMIN_ROUTE_PREFIX) && !getAdminStatusSync().isAdmin) {
            return next({ name: 'apps-v2' })
          }

          if (isCompassRoute(to)) {
            const { fetchStatus } = useCompassStatus()
            const status = await fetchStatus()

            if (!status || status.CompassAIEnabled !== true) {
              return next({ name: 'apps-v2' })
            }
          }

          // v2 onboarding lock: while the currently selected store needs
          // setup, only the wizard host (`apps-v2`) and the configured
          // exception routes (e.g. staff management) are reachable.
          try {
            const lockStore = useV2OnboardingLockStore()
            if (!lockStore.hasFetched) {
              await lockStore.refresh()
            }
            if (lockStore.isLocked && !lockStore.isRouteAllowed(to.name)) {
              return next({ name: 'apps-v2' })
            }
          } catch (lockError) {
            // Fail open -- never block navigation due to a lock-store error.
            console.debug('[Router Guard] v2 onboarding lock check failed:', lockError)
          }

          // Session is valid, allow navigation
          return next()
        }
      }

      // Session invalid or check failed - redirect to login
      console.warn('[Router Guard] Session check failed, redirecting to login')
      clearAuth()
      
      return next({
        name: 'login',
        query: { to: to.fullPath !== '/' ? to.fullPath : undefined },
      })

    } catch (error) {
      // Network error or server down
      console.error('[Router Guard] Session check error:', error)
      clearAuth()
      
      return next({
        name: 'login',
        query: { to: to.fullPath !== '/' ? to.fullPath : undefined },
      })
    }
  })
}
